import { getAccountCapabilities } from '@/features/ctrls-and-caps/get-account-capabilities'
import { getPlayerAccountStateOptions } from '@/apis/pam-account-api'
import { FrontendExperience } from '@/features/ctrls-and-caps/frontend-experience'
import { redirect } from '@tanstack/react-router'
import { QueryClient } from '@tanstack/react-query'

/**
 * This function should be used as a guard in the before load route methods.
 *
 * It asserts that player has CanPlay capability and if not it redirects to the locked page
 */
export const assertCanPlayBeforeLoad = async (queryClient: QueryClient) => {
  const { canPlay } = await getAccountCapabilities()

  if (canPlay) {
    return
  }

  // If Player has `canPlay: false` we need to fetch frontend experiences to understand WHY
  const state = await queryClient
    .ensureQueryData(getPlayerAccountStateOptions())
    // we don't want this error to be thrown into the beforeLoad function handler
    // we handle erro manually as the very end of this listing by fall-backing to the default error page
    .catch(() => null)

  const experiences = state?.frontendExperiences.map((e) => e.code) || []

  // We use location masking for these redirects so that all "account locked" pages
  // are rendered under the root path (`/`). This is needed for two reasons:
  //
  // 1. Cleaner UX — players don’t see auxiliary page names in the URL.
  // 2. Preventing players from getting stuck — if the player reloads while on `/`,
  //    `assertCanPlayBeforeLoad` runs again and may redirect them elsewhere if
  //    their capabilities have changed. Without masking, a reload on
  //    `/account-locked/error` would keep them stuck there since the check would
  //    never re-run.
  if (experiences.includes(FrontendExperience.SELF_EXCLUSION)) {
    throw redirect({ to: '/account-locked/self-exclusion', mask: { to: '/' } })
  }

  if (experiences.includes(FrontendExperience.TAKE_A_BREAK)) {
    throw redirect({ to: '/account-locked/take-a-break', mask: { to: '/' } })
  }

  if (experiences.includes(FrontendExperience.PLAYER_UNDER_AGE)) {
    throw redirect({ to: '/account-locked/under-age', mask: { to: '/' } })
  }

  if (experiences.includes(FrontendExperience.RESTRICTED_JURISDICTION)) {
    throw redirect({
      to: '/account-locked/restricted-jurisdiction',
      mask: { to: '/' },
    })
  }

  if (experiences.includes(FrontendExperience.MUST_VERIFY_NOW)) {
    throw redirect({
      to: '/account-locked/verification-required',
      mask: { to: '/' },
    })
  }

  // If we still don't know why player can't play, redirect to a generic error page
  throw redirect({ to: '/account-locked/error', mask: { to: '/' } })
}
